export type SoundType = 'reward' | 'alert' | 'online'

type ToneStep = { freq: number; duration: number; gain: number }

const SOUNDS: Record<SoundType, ToneStep[]> = {
  // Bright ascending arpeggio — cash-register feel
  reward: [
    { freq: 523.25, duration: 0.08, gain: 0.4 },
    { freq: 659.25, duration: 0.08, gain: 0.4 },
    { freq: 783.99, duration: 0.12, gain: 0.5 },
  ],
  // Short descending beep — offline alert
  alert: [
    { freq: 880, duration: 0.1, gain: 0.4 },
    { freq: 660, duration: 0.15, gain: 0.3 },
  ],
  // Short rising ping — device back online
  online: [
    { freq: 440, duration: 0.06, gain: 0.3 },
    { freq: 660, duration: 0.1,  gain: 0.4 },
  ],
}

export class SoundNotificationEngine {
  private static instance: SoundNotificationEngine | null = null
  private volume = 0.8
  private muted = false
  private ctx: AudioContext | null = null

  static getInstance(): SoundNotificationEngine {
    if (!SoundNotificationEngine.instance) {
      SoundNotificationEngine.instance = new SoundNotificationEngine()
    }
    return SoundNotificationEngine.instance
  }

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume))
    this.muted = this.volume === 0
  }

  mute(): void {
    this.muted = true
  }

  unmute(): void {
    this.muted = false
  }

  play(type: SoundType): void {
    if (this.muted || this.volume === 0) return
    try {
      if (!this.ctx) {
        this.ctx = new AudioContext()
      }
      const ctx = this.ctx
      let offset = 0
      for (const step of SOUNDS[type]) {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.type = 'sine'
        osc.frequency.setValueAtTime(step.freq, ctx.currentTime + offset)
        gain.gain.setValueAtTime(step.gain * this.volume, ctx.currentTime + offset)
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + offset + step.duration)
        osc.start(ctx.currentTime + offset)
        osc.stop(ctx.currentTime + offset + step.duration)
        offset += step.duration
      }
    } catch {
      // Web Audio API not available in this environment — ignore
    }
  }
}
